import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Smart entry routing — runs at most once per authenticated session.
 * Decision logic (fires only when user is authenticated AND currently on '/'):
 *   1. If no playbooks exist yet         → send to /brujula
 *   2. If ≥2 playbooks AND no active_playbook_id → send to /mis-rutas
 *   3. Else if a valid `ruta_last_route` was stored from a prior session → return there
 *   4. Else stay on '/' (Hoy)
 *
 * We key off `user?.id`: the effect runs whenever the user identity changes
 * (login/register), and a per-session flag in sessionStorage prevents it from
 * firing again during the same authenticated session.
 */
export default function SmartEntry({ children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const lastUserId = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { lastUserId.current = null; return; }

    // Only intervene on the natural post-login landing.
    if (location.pathname !== "/") return;

    // If we already decided for this user session, skip.
    if (lastUserId.current === user.id) return;
    if (sessionStorage.getItem("ruta_smart_routed") === user.id) {
      lastUserId.current = user.id;
      return;
    }

    lastUserId.current = user.id;

    (async () => {
      let target = "/";
      try {
        const [pbRes, stRes] = await Promise.all([api.get("/playbooks"), api.get("/settings")]);
        const playbooks = pbRes.data.playbooks || [];
        const activeId = stRes.data.active_playbook_id || null;
        const hasActive = activeId && playbooks.some((p) => p.id === activeId);
        const lastRoute = localStorage.getItem("ruta_last_route");
        const validLast = lastRoute && lastRoute !== "/" && lastRoute !== "/login" && lastRoute !== "/registro";

        if (playbooks.length === 0) {
          target = "/brujula";
        } else if (playbooks.length >= 2 && !hasActive) {
          target = "/mis-rutas";
        } else if (validLast) {
          target = lastRoute;
        } else {
          target = "/";
        }
      } catch {
        target = "/";
      } finally {
        sessionStorage.setItem("ruta_smart_routed", user.id);
        if (target !== "/") nav(target, { replace: true });
      }
    })();
  }, [user, loading, location.pathname, nav]);

  return children;
}
